"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef, useState } from "react";

type Screenshot = {
	title: string;
	imageUrl: string;
};

function PhoneFrame({
	screenshot,
	priority = false,
}: {
	screenshot: Screenshot;
	priority?: boolean;
}) {
	return (
		<div className="relative w-full overflow-hidden rounded-[2rem] border border-white/15 bg-[#080a0f] p-1.5 shadow-[0_28px_70px_-28px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.08)] sm:rounded-[2.4rem] sm:p-2">
			<div className="relative aspect-[440/956] overflow-hidden rounded-[1.65rem] bg-[#11141b] sm:rounded-[1.95rem]">
				<Image
					src={screenshot.imageUrl}
					alt={screenshot.title}
					fill
					priority={priority}
					className="object-contain object-center"
					sizes="(min-width: 1024px) 272px, (min-width: 640px) 36vw, 68vw"
				/>
			</div>
		</div>
	);
}

export default function PhoneScreenshotCarousel({
	screenshots,
}: {
	screenshots: Screenshot[];
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [direction, setDirection] = useState(1);
	const pointerStartX = useRef<number | null>(null);
	const reduceMotion = useReducedMotion();
	const hasMultiple = screenshots.length > 1;

	if (screenshots.length === 0) return null;

	const goTo = (index: number, requestedDirection?: -1 | 1) => {
		const nextActiveIndex =
			(index + screenshots.length) % screenshots.length;
		if (nextActiveIndex === activeIndex) return;

		const forwardDistance =
			(nextActiveIndex - activeIndex + screenshots.length) % screenshots.length;
		const backwardDistance =
			(activeIndex - nextActiveIndex + screenshots.length) % screenshots.length;

		setDirection(
			requestedDirection ?? (forwardDistance <= backwardDistance ? 1 : -1),
		);
		setActiveIndex(nextActiveIndex);
	};

	const previousIndex =
		(activeIndex - 1 + screenshots.length) % screenshots.length;
	const nextIndex = (activeIndex + 1) % screenshots.length;

	const finishSwipe = (clientX: number) => {
		if (pointerStartX.current === null) return;

		const distance = clientX - pointerStartX.current;
		pointerStartX.current = null;
		if (Math.abs(distance) < 48) return;

		goTo(
			distance > 0 ? activeIndex - 1 : activeIndex + 1,
			distance > 0 ? -1 : 1,
		);
	};

	return (
		<div
			role="region"
			aria-roledescription="carousel"
			aria-label="Mobile project screenshots"
			tabIndex={0}
			onKeyDown={(event) => {
				if (!hasMultiple) return;
				if (event.key === "ArrowLeft") {
					event.preventDefault();
					goTo(activeIndex - 1, -1);
				}
				if (event.key === "ArrowRight") {
					event.preventDefault();
					goTo(activeIndex + 1, 1);
				}
			}}
			onPointerDown={(event) => {
				pointerStartX.current = event.clientX;
			}}
			onPointerUp={(event) => finishSwipe(event.clientX)}
			onPointerCancel={() => {
				pointerStartX.current = null;
			}}
			className="relative mx-auto touch-pan-y py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-4 focus-visible:ring-offset-[#0b0d12] sm:py-6"
		>
			<div className="relative isolate">
				{hasMultiple ? (
					<>
						<motion.button
							key={`previous-${screenshots[previousIndex].imageUrl}`}
							type="button"
							onClick={() => goTo(previousIndex, -1)}
							aria-label={`View previous screenshot: ${screenshots[previousIndex].title}`}
							initial={reduceMotion ? false : { opacity: 0 }}
							animate={{ opacity: 0.35 }}
							transition={{ duration: reduceMotion ? 0 : 0.4 }}
							className="absolute left-[8%] top-1/2 z-0 hidden w-[28%] max-w-[13rem] -translate-y-1/2 scale-[0.88] cursor-pointer overflow-hidden rounded-[2.4rem] grayscale-[35%] transition-[filter] duration-300 hover:brightness-125 motion-reduce:transition-none sm:block lg:left-[20%]"
						>
							<PhoneFrame screenshot={screenshots[previousIndex]} />
							<span className="pointer-events-none absolute inset-0 bg-[#050706]/45" />
						</motion.button>

						<motion.button
							key={`next-${screenshots[nextIndex].imageUrl}`}
							type="button"
							onClick={() => goTo(nextIndex, 1)}
							aria-label={`View next screenshot: ${screenshots[nextIndex].title}`}
							initial={reduceMotion ? false : { opacity: 0 }}
							animate={{ opacity: 0.35 }}
							transition={{ duration: reduceMotion ? 0 : 0.4 }}
							className="absolute right-[8%] top-1/2 z-0 hidden w-[28%] max-w-[13rem] -translate-y-1/2 scale-[0.88] cursor-pointer overflow-hidden rounded-[2.4rem] grayscale-[35%] transition-[filter] duration-300 hover:brightness-125 motion-reduce:transition-none sm:block lg:right-[20%]"
						>
							<PhoneFrame screenshot={screenshots[nextIndex]} />
							<span className="pointer-events-none absolute inset-0 bg-[#050706]/45" />
						</motion.button>
					</>
				) : null}

				<AnimatePresence initial={false} custom={direction} mode="popLayout">
					<motion.div
						key={`${screenshots[activeIndex].imageUrl}-${activeIndex}`}
						custom={direction}
						variants={{
							enter: (slideDirection: number) => ({
								x: reduceMotion ? 0 : slideDirection > 0 ? "12%" : "-12%",
								opacity: reduceMotion ? 1 : 0,
								scale: reduceMotion ? 1 : 0.97,
							}),
							center: { x: 0, opacity: 1, scale: 1 },
							exit: (slideDirection: number) => ({
								x: reduceMotion ? 0 : slideDirection > 0 ? "-12%" : "12%",
								opacity: reduceMotion ? 1 : 0,
								scale: reduceMotion ? 1 : 0.97,
							}),
						}}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{
							duration: reduceMotion ? 0 : 0.48,
							ease: [0.22, 1, 0.36, 1],
						}}
						aria-live="polite"
						aria-label={`Screenshot ${activeIndex + 1} of ${screenshots.length}`}
						className="relative z-10 mx-auto w-[68%] max-w-[17rem] sm:w-[36%]"
					>
						<PhoneFrame screenshot={screenshots[activeIndex]} priority />
					</motion.div>
				</AnimatePresence>

				{hasMultiple ? (
					<>
						<button
							type="button"
							onClick={() => goTo(activeIndex - 1, -1)}
							aria-label="Previous screenshot"
							className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/70 shadow-[0_14px_34px_rgba(0,0,0,0.42)] backdrop-blur transition-colors duration-200 hover:border-white/40 hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 sm:left-[12%] sm:h-11 sm:w-11 lg:left-[26%]"
						>
							<ChevronLeft aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
						</button>
						<button
							type="button"
							onClick={() => goTo(activeIndex + 1, 1)}
							aria-label="Next screenshot"
							className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/70 shadow-[0_14px_34px_rgba(0,0,0,0.42)] backdrop-blur transition-colors duration-200 hover:border-white/40 hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 sm:right-[12%] sm:h-11 sm:w-11 lg:right-[26%]"
						>
							<ChevronRight aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
						</button>
					</>
				) : null}
			</div>

			{hasMultiple ? (
				<div className="mt-5 flex flex-wrap items-center justify-center gap-0.5 sm:mt-7 sm:gap-1">
					{screenshots.map((screenshot, index) => {
						const isActive = index === activeIndex;

						return (
							<button
								key={`${screenshot.imageUrl}-${index}`}
								type="button"
								onClick={() => goTo(index)}
								aria-label={`Go to screenshot ${index + 1}: ${screenshot.title}`}
								aria-current={isActive ? "true" : undefined}
								className="group flex h-7 w-7 cursor-pointer items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
							>
								<motion.span
									initial={false}
									animate={{
										width: isActive ? 24 : 8,
										backgroundColor: isActive
											? "rgba(244,244,245,1)"
											: "rgba(255,255,255,0.2)",
									}}
									transition={
										reduceMotion
											? { duration: 0 }
											: { type: "spring", stiffness: 380, damping: 30, mass: 0.7 }
									}
									className="block h-2 rounded-full transition-[filter] duration-200 group-hover:brightness-150"
								/>
							</button>
						);
					})}
				</div>
			) : null}

			<p className={`${hasMultiple ? "mt-1" : "mt-5"} text-center text-sm text-zinc-400`}>
				{screenshots[activeIndex].title}
			</p>
		</div>
	);
}
